import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { InteractionStatus } from "@azure/msal-browser";
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { msalInstance } from "./auth/msalInstance";
import { acquireIdToken, AUTH_REQUIRED_EVENT } from "./auth/acquireIdToken";
import LoginPage from "./pages/LoginPage";
import SessionExpiredPage from "./pages/SessionExpiredPage";
import ContactsPage from "./pages/ContactsPage";
import PersoneelPage from "./pages/PersoneelPage";
import LeveranciersPage from "./pages/LeveranciersPage";
import ExterneContactenPage from "./pages/ExterneContactenPage";
import ImportContactsPage from "./pages/ImportContactsPage";
import GroupsPage from "./pages/GroupsPage";
import GroupDetailPage from "./pages/GroupDetailPage";
import AanvragenPage from "./pages/AanvragenPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminAuditLogPage from "./pages/AdminAuditLogPage";
import BeheerToolsPage from "./pages/BeheerToolsPage";
import MailerLiteInboxPage from "./pages/MailerLiteInboxPage";
import MailerLiteImportPage from "./pages/MailerLiteImportPage";
import BezoekersbestandPage from "./pages/BezoekersbestandPage";
import ImportBezoekersPage from "./pages/ImportBezoekersPage";
import WerkgroepIntakePage from "./pages/WerkgroepIntakePage";
import AlleContactenPage from "./pages/AlleContactenPage";
import Layout from "./components/Layout";
import { ZendContactenProvider } from "./context/ZendContactenContext";
import { api } from "./lib/api";
import type { User } from "@domaddress/shared";
import { canViewActivityLog, hasOrgWideContactReadAccess } from "@domaddress/shared";

/** Paths that are accessible without Microsoft login — MSAL must not run on these. */
const PUBLIC_PATHS = ["/werkgroepen-en-leden"];

function isPublicPathname(pathname: string): boolean {
  return PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

type AuthPhase = "loading" | "ready" | "signed-out" | "needs-interactive";

function AppRoutes() {
  const isAuthenticated = useIsAuthenticated();
  const { accounts, inProgress } = useMsal();
  const account = accounts[0] ?? null;
  /** Startup en de terugkeer van Microsoft zijn nog bezig. Dan is "niet ingelogd" nog niet waar. */
  const msalReady =
    inProgress !== InteractionStatus.Startup && inProgress !== InteractionStatus.HandleRedirect;
  const [user, setUser] = useState<User | null>(null);
  const [phase, setPhase] = useState<AuthPhase>("loading");
  const loginClientEventSent = useRef(false);

  useEffect(() => {
    const onAuthRequired = () => setPhase("needs-interactive");
    window.addEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
  }, []);

  useEffect(() => {
    if (!msalReady) return;
    if (!isAuthenticated || !account) {
      loginClientEventSent.current = false;
      setUser(null);
      setPhase("signed-out");
      return;
    }
    let cancelled = false;
    setPhase("loading");
    void (async () => {
      const result = await acquireIdToken({ allowInteractive: true });
      if (cancelled || result.redirecting) return;
      if (!result.token) {
        setUser(null);
        setPhase("needs-interactive");
        return;
      }
      try {
        const me = await api.get<{ data: User }>("/api/me");
        if (cancelled) return;
        setUser(me.data.data);
        if (!loginClientEventSent.current) {
          loginClientEventSent.current = true;
          void api.post("/api/audit/client-event", { event: "auth.login" }).catch(() => {});
        }
        setPhase("ready");
      } catch {
        if (cancelled) return;
        setUser(null);
        setPhase("needs-interactive");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [msalReady, isAuthenticated, account]);

  /**
   * Houd het id-token geldig.
   * De timer dekt normaal gebruik. visibilitychange dekt een tab die uren op de achtergrond stond:
   * browsers zetten timers dan bijna stil. Een mislukking wist het token niet; Microsoft-aanmelding
   * gaat dan vanzelf, of het aanmeldscherm verschijnt.
   */
  useEffect(() => {
    if (phase !== "ready" || !account) return;

    async function tryRefresh(allowInteractive: boolean) {
      const result = await acquireIdToken({ allowInteractive });
      if (!result.token && !result.redirecting) {
        setPhase("needs-interactive");
      }
    }

    const tick = window.setInterval(() => {
      void tryRefresh(false);
    }, 45 * 60 * 1000);

    function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        void tryRefresh(true);
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(tick);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [phase, account]);

  if (!msalReady || phase === "loading") {
    return (
      <div className="page" style={{ minHeight: "50vh" }}>
        <div className="loading-spinner" />
        <p className="page-subtitle" style={{ textAlign: "center", marginTop: "1rem" }}>
          Even geduld, aanmelding bij de server…
        </p>
      </div>
    );
  }

  if (phase === "needs-interactive") {
    return <SessionExpiredPage />;
  }

  if (!isAuthenticated || phase === "signed-out") {
    return <LoginPage />;
  }

  return (
    <ZendContactenProvider user={user}>
      <Layout user={user}>
        <Routes>
          <Route path="/" element={<Navigate to="/vrijwilligers" replace />} />
          {/* Nieuwe lijsten */}
          <Route path="/vrijwilligers" element={<ContactsPage user={user} />} />
          <Route path="/personeel" element={<PersoneelPage user={user} />} />
          <Route path="/externe-contacten" element={<ExterneContactenPage user={user} />} />
          <Route path="/leveranciers" element={<LeveranciersPage user={user} />} />
          <Route path="/dominicusgangers" element={<BezoekersbestandPage user={user} />} />
          {/* Import routes */}
          <Route path="/vrijwilligers/importeren" element={<ImportContactsPage />} />
          <Route path="/personeel/importeren" element={<ImportContactsPage apiPath="/api/personeel" />} />
          <Route path="/leveranciers/importeren" element={<ImportContactsPage apiPath="/api/leveranciers" />} />
          <Route path="/dominicusgangers/import" element={<ImportBezoekersPage />} />
          {/* Backward-compat redirects */}
          <Route path="/medewerkers" element={<Navigate to="/vrijwilligers" replace />} />
          <Route path="/medewerkers/importeren" element={<Navigate to="/vrijwilligers/importeren" replace />} />
          <Route path="/bezoekersbestand" element={<Navigate to="/dominicusgangers" replace />} />
          <Route path="/bezoekersbestand/import" element={<Navigate to="/dominicusgangers/import" replace />} />
          <Route path="/contacts" element={<Navigate to="/vrijwilligers" replace />} />
          <Route path="/contacts/import" element={<Navigate to="/vrijwilligers/importeren" replace />} />
          <Route path="/groups" element={<GroupsPage />} />
          <Route path="/groups/:id" element={<GroupDetailPage />} />
          <Route
            path="/alle-contacten"
            element={
              user && hasOrgWideContactReadAccess(user) ? (
                <AlleContactenPage user={user} />
              ) : (
                <Navigate to="/vrijwilligers" replace />
              )
            }
          />
          <Route path="/aanvragen" element={<AanvragenPage />} />
          {user && canViewActivityLog(user) && (
            <>
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/audit" element={<AdminAuditLogPage />} />
              <Route path="/admin/tools" element={<BeheerToolsPage />} />
              <Route path="/admin/mailerlite-inbox" element={<MailerLiteInboxPage />} />
              <Route path="/admin/mailerlite-import" element={<MailerLiteImportPage />} />
            </>
          )}
          <Route path="*" element={<Navigate to="/vrijwilligers" replace />} />
        </Routes>
      </Layout>
    </ZendContactenProvider>
  );
}

export default function App() {
  // Public paths bypass MSAL entirely — check window.location before any hook or MsalProvider runs.
  if (isPublicPathname(window.location.pathname)) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/werkgroepen-en-leden" element={<WerkgroepIntakePage />} />
          <Route path="*" element={<WerkgroepIntakePage />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </MsalProvider>
  );
}
